import { useEffect, useState } from 'react';

import { useAuth, useConversionHistory } from '@/hooks';
import { historyMeta, blurActiveElement } from '@/utils';
import { HoneypotInput, Pagination, PrimaryAuthForm } from '@/components';

import { Meta } from '../segments';

import {
  getFromDate,
  STATUS_OPTIONS,
  DEFAULT_FILTERS,
  PLATFORM_OPTIONS,
  DATE_RANGE_OPTIONS,
} from './helpers';
import ConversionRow from './ConversionRow';

const History = () => {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const { isAuthorized, onUnauthorized } = useAuth();
  const [botDetected, setBotDetected] = useState(false);
  const { data, error, isLoading, getHistory } = useConversionHistory({
    onUnauthorized,
  });

  const activeFilters = Object.fromEntries(
    Object.entries(filters)
      .filter(([k, v]) => v !== '' && k !== 'dateRange')
      .concat(
        filters.dateRange ? [['from', getFromDate(filters.dateRange)]] : []
      )
  );

  const hasActiveFilters = Object.keys(filters).some(
    (k) => filters[k] !== DEFAULT_FILTERS[k]
  );

  useEffect(() => {
    if (isAuthorized) {
      getHistory(page, activeFilters);
    }
  }, [page, filters, getHistory, isAuthorized]);

  const handlePageChange = (newPage) => {
    setPage(newPage);

    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (key, event) => {
    blurActiveElement();

    setFilters((prev) => ({ ...prev, [key]: event.target.value }));

    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);

    setPage(1);
  };

  return (
    <>
      <Meta {...historyMeta} robots />

      <main
        className="container py-4"
        style={{ minHeight: 'calc(100vh - 410px)' }}
      >
        <HoneypotInput onBotDetected={setBotDetected} />

        <div className="text-center mb-4">
          <h1 className="mb-2">Conversion History</h1>

          <p className="text-muted m-0">
            View your playlist conversion history
          </p>
        </div>

        <div className="row justify-content-center">
          <div className="col-lg-8">
            {isAuthorized ? (
              <>
                <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                  <select
                    value={filters.status}
                    aria-label="Filter by status"
                    className="form-select form-select-sm w-auto"
                    onChange={handleFilterChange.bind(null, 'status')}
                  >
                    {STATUS_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={filters.source}
                    aria-label="Filter by source platform"
                    className="form-select form-select-sm w-auto"
                    onChange={handleFilterChange.bind(null, 'source')}
                  >
                    {PLATFORM_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        From: {label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={filters.target}
                    aria-label="Filter by target platform"
                    className="form-select form-select-sm w-auto"
                    onChange={handleFilterChange.bind(null, 'target')}
                  >
                    {PLATFORM_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        To: {label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={filters.dateRange}
                    aria-label="Filter by date range"
                    className="form-select form-select-sm w-auto"
                    onChange={handleFilterChange.bind(null, 'dateRange')}
                  >
                    {DATE_RANGE_OPTIONS.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>

                  {hasActiveFilters && (
                    <button
                      onClick={handleClearFilters}
                      className="btn btn-sm btn-outline-secondary"
                    >
                      Clear filters
                    </button>
                  )}
                </div>

                {isLoading && (
                  <div className="text-center py-5">
                    <i
                      aria-hidden="true"
                      style={{ fontSize: '1.5rem' }}
                      className="fas fa-spinner fa-spin text-accent mb-3"
                    />

                    <p className="text-muted mb-0">Loading conversions...</p>
                  </div>
                )}

                {!isLoading && error && (
                  <div className="border rounded p-4 text-center">
                    <p className="text-danger mb-2">{error}</p>

                    <button
                      className="btn btn-sm btn-accent"
                      onClick={() => getHistory(page, activeFilters)}
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {!isLoading &&
                  !error &&
                  data &&
                  data.conversions.length === 0 && (
                    <div className="border rounded p-4 text-center">
                      <i
                        aria-hidden="true"
                        style={{ fontSize: '2rem' }}
                        className="fas fa-history text-muted mb-3 d-block"
                      />

                      <p className="text-muted mb-0">
                        {hasActiveFilters
                          ? 'No conversions match the selected filters.'
                          : 'No conversions yet. Start by transferring a playlist!'}
                      </p>
                    </div>
                  )}

                {!isLoading &&
                  !error &&
                  data &&
                  data.conversions.length > 0 && (
                    <>
                      {data.conversions.map((conversion) => (
                        <ConversionRow
                          key={conversion.id}
                          conversion={conversion}
                          onUnauthorized={onUnauthorized}
                        />
                      ))}

                      <Pagination
                        pagination={data.pagination}
                        onPageChange={handlePageChange}
                      />
                    </>
                  )}
              </>
            ) : (
              <PrimaryAuthForm botDetected={botDetected} />
            )}
          </div>
        </div>
      </main>
    </>
  );
};

export default History;
